/**
 * 검토 모드에서 IXD 테이블 헤더/패딩헤더 클릭 시 어떤 데이터 셀들이 선택돼야
 * 하는지 결정한다.
 *
 * 규칙(클릭한 표 내부만):
 * - 컬럼 헤더: 헤더가 차지하는 가로 영역 아래의 모든 데이터 셀
 * - 행 헤더: 헤더가 차지하는 세로 영역 옆의 모든 데이터 셀
 * - 패딩 헤더 (좌상단 코너): 표 전체의 데이터 셀
 *
 * 구현 메모:
 * - 헤더의 시각적 역할은 `<th scope>` 가 아닌 DOM 구조로 판단한다. transpose 된
 *   표에서는 scope 가 시각적 위치와 어긋나기 때문이다. 자세한 규칙은
 *   `determineHeaderRole` 참고.
 * - DOM 의 `tr.children[colIndex]` 는 `rowspan`/`colspan` 이 있을 때 논리 column
 *   위치와 어긋난다(이전 행의 spanning 셀이 다음 행의 colIndex 를 shift 시키기
 *   때문). 따라서 헤더 vs 데이터 셀의 실제 렌더 직사각형 교차로 매칭하는
 *   geometric 접근을 사용한다 — colspan/rowspan 을 자연스럽게 흡수한다.
 */
import { determineHeaderRole } from "./header-role";

const CENTER_TOLERANCE_PX = 0.5;

/**
 * 헤더/패딩 헤더(th) 클릭 시 대응되는 데이터 셀 **요소**들을 반환한다.
 */
export const collectIxdCellsForHeader = (
  target: HTMLElement,
): HTMLElement[] | null => {
  const th = target.closest<HTMLElement>("th");
  if (!th) return null;

  const table = th.closest("table");
  if (!table) return null;

  const role = determineHeaderRole(th);
  if (!role) return null;

  if (role === "padding") {
    return Array.from(table.querySelectorAll<HTMLElement>("[data-cell-id]"));
  }

  const thRect = th.getBoundingClientRect();
  const dataCells = Array.from(
    table.querySelectorAll<HTMLElement>("[data-cell-id]"),
  );

  if (role === "col") {
    return dataCells.filter((cell) => {
      const r = cell.getBoundingClientRect();
      const centerX = (r.left + r.right) / 2;
      return (
        centerX >= thRect.left - CENTER_TOLERANCE_PX &&
        centerX <= thRect.right + CENTER_TOLERANCE_PX
      );
    });
  }

  // role === "row"
  return dataCells.filter((cell) => {
    const r = cell.getBoundingClientRect();
    const centerY = (r.top + r.bottom) / 2;
    return (
      centerY >= thRect.top - CENTER_TOLERANCE_PX &&
      centerY <= thRect.bottom + CENTER_TOLERANCE_PX
    );
  });
};
